import React, { createContext, useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import { DisclaimerPage, FAQPage, LegalPage } from './pages/LegalPages';
import { Home, Scale, PieChart, HeartPulse, Briefcase, Sparkles, Wrench, GraduationCap, Car, Shield, UtensilsCrossed, LayoutGrid } from 'lucide-react';
import { SPRegisterPage, ProviderDashboard, AdminListingApprovals, ProviderProfilePage } from './pages/ProviderPortal';
import { ServicesPage } from './pages/ServicesPage';
import { AdminCategories, AdminProviders, AdminMembers, AdminSubscriptions, AdminFeedbacks, AdminAnalytics } from './pages/AdminPages';

const INDIAN_STATES = ['Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Delhi','Goa','Gujarat','Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh','Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland','Odisha','Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura','Uttar Pradesh','Uttarakhand','West Bengal','Chandigarh','Dadra and Nagar Haveli','Daman and Diu','Jammu and Kashmir','Ladakh','Lakshadweep','Puducherry','Andaman and Nicobar Islands'];

const CITY_STATE_MAP = {
  'Gurgaon': 'Haryana', 'Faridabad': 'Haryana', 'Panipat': 'Haryana', 'Ambala': 'Haryana',
  'Rohtak': 'Haryana', 'Hisar': 'Haryana', 'Karnal': 'Haryana', 'Sonipat': 'Haryana',
  'Panchkula': 'Haryana', 'Bhiwani': 'Haryana', 'Sirsa': 'Haryana', 'Jind': 'Haryana',
  'Rewari': 'Haryana', 'Manesar': 'Haryana', 'Sohna': 'Haryana', 'Dharuhera': 'Haryana',
  'Mumbai': 'Maharashtra', 'Pune': 'Maharashtra', 'Nagpur': 'Maharashtra', 'Thane': 'Maharashtra',
  'Navi Mumbai': 'Maharashtra',
  'New Delhi': 'Delhi', 'Delhi': 'Delhi',
  'Bengaluru': 'Karnataka', 'Mysuru': 'Karnataka',
  'Hyderabad': 'Telangana', 'Warangal': 'Telangana',
  'Chennai': 'Tamil Nadu', 'Coimbatore': 'Tamil Nadu',
  'Kolkata': 'West Bengal',
  'Ahmedabad': 'Gujarat', 'Surat': 'Gujarat', 'Vadodara': 'Gujarat',
  'Jaipur': 'Rajasthan', 'Udaipur': 'Rajasthan', 'Jodhpur': 'Rajasthan',
  'Lucknow': 'Uttar Pradesh', 'Noida': 'Uttar Pradesh', 'Ghaziabad': 'Uttar Pradesh',
  'Greater Noida': 'Uttar Pradesh', 'Agra': 'Uttar Pradesh', 'Varanasi': 'Uttar Pradesh',
  'Chandigarh': 'Chandigarh', 'Mohali': 'Punjab', 'Ludhiana': 'Punjab', 'Amritsar': 'Punjab',
  'Bhopal': 'Madhya Pradesh', 'Indore': 'Madhya Pradesh',
  'Patna': 'Bihar', 'Ranchi': 'Jharkhand', 'Bhubaneswar': 'Odisha',
  'Dehradun': 'Uttarakhand', 'Shimla': 'Himachal Pradesh',
  'Kochi': 'Kerala', 'Thiruvananthapuram': 'Kerala',
  'Goa': 'Goa', 'Raipur': 'Chhattisgarh',
};
const MAJOR_CITIES = Object.keys(CITY_STATE_MAP).sort();
const STATE_CITIES_MAP = {};
Object.entries(CITY_STATE_MAP).forEach(([city, state]) => {
  if (!STATE_CITIES_MAP[state]) STATE_CITIES_MAP[state] = [];
  STATE_CITIES_MAP[state].push(city);
});
Object.values(STATE_CITIES_MAP).forEach(arr => arr.sort());

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

export { INDIAN_STATES, CITY_STATE_MAP, MAJOR_CITIES, STATE_CITIES_MAP, isValidEmail };

export const lookupPincode = async (pin) => {
  try {
    const res = await fetch(`https://api.postalpincode.in/pincode/${pin}`);
    const data = await res.json();
    const po = data?.[0]?.Status === 'Success' ? data[0].PostOffice?.[0] : null;
    return po ? { city: po.District, state: po.State } : null;
  } catch { return null; }
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Token management for auth
const getToken = () => localStorage.getItem('privenet_token');
const setToken = (token) => { if (token) localStorage.setItem('privenet_token', token); };
const clearToken = () => localStorage.removeItem('privenet_token');

// Axios interceptor to always send token as Bearer header
axios.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  config.withCredentials = true;
  return config;
});

const secrets_hex = () => Array.from(crypto.getRandomValues(new Uint8Array(8))).map(b => b.toString(16).padStart(2, '0')).join('');

// ─── Auth Context ───
const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

function formatError(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { checkAuth(); }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
    } catch { setUser(false); clearToken(); }
    finally { setLoading(false); }
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      if (data.token) setToken(data.token);
      setUser(data);
      return { success: true, data };
    } catch (e) {
      return { success: false, error: formatError(e.response?.data?.detail) };
    }
  }, []);

  const register = useCallback(async (formData) => {
    try {
      const { data } = await axios.post(`${API}/auth/register`, formData);
      if (data.token) setToken(data.token);
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatError(e.response?.data?.detail) };
    }
  }, []);

  const logout = useCallback(async () => {
    await axios.post(`${API}/auth/logout`, {}).catch(() => {});
    clearToken();
    setUser(false);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') return <Navigate to="/dashboard" replace />;
  return children;
};

const ProviderDashboardWrapper = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  if (user?.role !== 'service_provider') return <Navigate to="/dashboard" replace />;
  return <ProviderDashboard user={user} onLogout={async () => { await logout(); navigate('/login'); }} />;
};

const LoadingScreen = () => (
  <div className="min-h-screen bg-[#05050A] flex items-center justify-center">
    <div className="text-[#C5A059] text-lg" style={{ fontFamily: 'Outfit' }}>Loading...</div>
  </div>
);

// ─── App ───
function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/services" element={<ProtectedRoute><ServicesPage Header={Header} /></ProtectedRoute>} />
          <Route path="/referrals" element={<ProtectedRoute><ReferralsPage /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          <Route path="/subscription" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />
          <Route path="/admin" element={<AdminRoute><AdminPanel /></AdminRoute>} />
          <Route path="/register-provider" element={<SPRegisterPage onRegister={(d) => {}} />} />
          <Route path="/provider-dashboard" element={<ProtectedRoute><ProviderDashboardWrapper /></ProtectedRoute>} />
          <Route path="/provider-profile" element={<ProtectedRoute><ProviderProfilePage /></ProtectedRoute>} />
          <Route path="/disclaimer" element={<DisclaimerPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/legal" element={<LegalPage />} />
          <Route path="/verify-membership" element={<VerifyMembershipPage />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

// ═══════════════════════════════════════════
// SHARED COMPONENTS
// ═══════════════════════════════════════════

const VerifiedBadge = () => (
  <svg className="w-5 h-5 text-[#C5A059] inline-block ml-1" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
  </svg>
);

const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';

  return (
    <header className="fixed top-0 w-full z-50 backdrop-blur-2xl bg-[#05050A]/70 border-b border-white/[0.04]">
      <div className="px-6 md:px-12 lg:px-24 py-5 flex items-center justify-between">
        <button onClick={() => navigate(isAdmin ? '/admin' : '/dashboard')} className="text-2xl font-light tracking-tight" style={{ fontFamily: 'Cormorant Garamond' }} data-testid="header-logo">PriveNet</button>
        <nav className="flex items-center gap-6 text-sm">
          {isAdmin ? (
            <button data-testid="header-admin-link" onClick={() => navigate('/admin')} className="text-white/60 hover:text-white transition-colors">Admin Panel</button>
          ) : (
            <>
              <button data-testid="header-services-link" onClick={() => navigate('/dashboard')} className="text-white/60 hover:text-white transition-colors">Home</button>
              <button data-testid="header-trusted-partners-link" onClick={() => navigate('/services')} className="text-white/60 hover:text-white transition-colors">Trusted Partners</button>
              <button data-testid="header-invite-link" onClick={() => navigate('/referrals')} className="text-white/60 hover:text-white transition-colors">Invite Friends</button>
              <button data-testid="header-profile-link" onClick={() => navigate('/profile')} className="text-white/60 hover:text-white transition-colors">Profile</button>
            </>
          )}
          <button data-testid="header-logout-button" onClick={async () => { await logout(); navigate('/login'); }} className="border border-white/20 text-white px-5 py-2 text-sm hover:border-white/60 transition-colors">Logout</button>
        </nav>
      </div>
    </header>
  );
};

const GoldInput = ({ label, required, ...props }) => (
  <div>
    <label className="block text-sm text-white/60 mb-2">{label}{required && <span className="text-[#C5A059] ml-1">*</span>}</label>
    <input {...props} className="w-full bg-transparent border-b border-white/20 py-3 text-white focus:border-[#C5A059] focus:outline-none transition-colors placeholder:text-white/20" />
  </div>
);

const GoldButton = ({ children, variant = 'primary', ...props }) => {
  const base = variant === 'primary'
    ? 'bg-[#C5A059] text-black hover:bg-[#E2C382]'
    : 'border border-white/20 text-white hover:border-white/60';
  return <button {...props} className={`px-8 py-3 text-sm font-medium tracking-wide transition-colors disabled:opacity-50 ${base} ${props.className || ''}`}>{children}</button>;
};

// Membership Verification Component (used on landing page)
const MembershipVerifier = () => {
  const [code, setCode] = useState('');
  const [result, setResult] = useState(null);
  const [checking, setChecking] = useState(false);

  const verify = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setChecking(true); setResult(null);
    try {
      const { data } = await axios.get(`${API}/verify-membership/${code.trim()}`);
      setResult(data);
    } catch { setResult({ valid: false, message: 'Error checking membership' }); }
    setChecking(false);
  };

  return (
    <div data-testid="membership-verifier">
      <form onSubmit={verify} className="flex gap-3 justify-center mb-6">
        <input data-testid="verify-code-input" value={code} onChange={e => setCode(e.target.value)} placeholder="Enter Membership ID" className="bg-transparent border-b border-white/20 py-3 px-4 text-white focus:border-[#C5A059] focus:outline-none w-64 text-center" />
        <GoldButton data-testid="verify-code-btn" type="submit" disabled={checking}>{checking ? 'Checking...' : 'Verify'}</GoldButton>
      </form>
      {result && (
        <div className={`p-4 border text-sm ${result.valid ? 'border-green-500/30 bg-green-500/5 text-green-400' : 'border-red-500/30 bg-red-500/5 text-red-400'}`} data-testid="verify-result">
          {result.valid ? (
            <div>
              <div className="font-medium mb-1">Valid Membership</div>
              <div className="text-white/60">Member: {result.member_name} | {result.city}, {result.state}</div>
              <div className="text-white/60">Active Subscription: {result.has_active_subscription ? 'Yes' : 'No'}</div>
            </div>
          ) : (
            <div>{result.message}</div>
          )}
        </div>
      )}
    </div>
  );
};

const VerifyMembershipPage = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#05050A] text-white flex items-center justify-center px-6" style={{ fontFamily: 'Outfit' }}>
      <div className="w-full max-w-lg text-center">
        <button onClick={() => navigate('/')} className="text-2xl font-light mb-12 inline-block" style={{ fontFamily: 'Cormorant Garamond' }} data-testid="verify-page-logo">PriveNet</button>
        <div className="text-xs uppercase tracking-[0.2em] text-[#C5A059] font-medium mb-3">For Service Providers</div>
        <h1 className="text-3xl lg:text-4xl font-light text-white mb-8" style={{ fontFamily: 'Cormorant Garamond' }} data-testid="verify-page-heading">Verify Member Status</h1>
        <MembershipVerifier />
        <button onClick={() => navigate(-1)} className="mt-10 text-sm text-white/40 hover:text-white transition-colors" data-testid="verify-page-back">&larr; Go Back</button>
      </div>
    </div>
  );
};

const ContactForm = () => {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState('');
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true); setStatus('');
    try {
      await axios.post(`${API}/contact`, form);
      setStatus('Message sent! We will get back to you shortly.');
      setForm({ name: '', email: '', message: '' });
    } catch (err) {
      setStatus(err.response?.data?.detail || 'Failed to send. Please try again.');
    }
    setSending(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 text-left" data-testid="contact-form">
      {status && <div className={`p-3 text-sm ${status.includes('sent') ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>{status}</div>}
      <div className="grid md:grid-cols-2 gap-4">
        <input data-testid="contact-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Your Name" required className="w-full bg-[#05050A] border border-white/10 py-3 px-4 text-white text-sm focus:border-[#C5A059] focus:outline-none placeholder:text-white/30" />
        <input data-testid="contact-email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="Your Email" required className="w-full bg-[#05050A] border border-white/10 py-3 px-4 text-white text-sm focus:border-[#C5A059] focus:outline-none placeholder:text-white/30" />
      </div>
      <textarea data-testid="contact-message" value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} placeholder="Your Message" required className="w-full bg-[#05050A] border border-white/10 py-3 px-4 text-white text-sm focus:border-[#C5A059] focus:outline-none h-24 resize-none placeholder:text-white/30" />
      <button type="submit" disabled={sending} className="w-full bg-[#C5A059] text-black py-3 text-sm font-medium hover:bg-[#E2C382] transition-colors disabled:opacity-50" data-testid="contact-submit">
        {sending ? 'Sending...' : 'Send Message'}
      </button>
      <p className="text-white/30 text-xs text-center">Or email us directly at support@privenet.in</p>
    </form>
  );
};

// ═══════════════════════════════════════════
// LANDING PAGE
// ═══════════════════════════════════════════

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (user && user.role === 'admin') navigate('/admin');
    else if (user && user.role === 'service_provider') navigate('/provider-dashboard');
    else if (user) navigate('/dashboard');
  }, [user, navigate]);

  const categories = [
    { icon: '\u2696\uFE0F', name: 'Legal & Financial', desc: 'Legal and financial professionals trusted for life\'s important decisions.' },
    { icon: '\uD83C\uDFE1', name: 'Home & Interiors', desc: 'Interior designers, architects, and home specialists vetted through referrals.' },
    { icon: '\uD83C\uDFE5', name: 'Health & Wellness', desc: 'Health professionals members confidently recommend to family and friends.', featured: true },
    { icon: '\uD83C\uDF93', name: 'Education & Family', desc: 'Tutors, counselors, and family services with proven track records.' },
    { icon: '\u2708\uFE0F', name: 'Travel & Lifestyle', desc: 'Travel planners, event managers, and lifestyle consultants.' },
    { icon: '\u2638\uFE0F', name: 'Spirituality', desc: 'Spiritual guidance, Bhandara, Pooja, Kirtan, rituals, and meaningful experiences.' },
  ];

  return (
    <div className="min-h-screen bg-[#05050A] text-white" style={{ fontFamily: 'Outfit' }}>
      {/* Nav */}
      <header className="fixed top-0 w-full z-50 backdrop-blur-2xl bg-[#05050A]/70 border-b border-white/[0.04]">
        <div className="px-6 md:px-12 lg:px-24 py-6 flex items-center justify-between">
          <div className="text-2xl font-light" style={{ fontFamily: 'Cormorant Garamond' }}>PriveNet</div>
          <nav className="flex items-center gap-6">
            {[
              { label: 'Why', target: 'why' },
              { label: 'How', target: 'how' },
              { label: 'Categories', target: 'categories' },
              { label: 'Pricing', target: 'pricing' },
            ].map(({ label, target }) => (
              <button key={target} data-testid={`nav-${target}`} onClick={() => document.getElementById(target)?.scrollIntoView({ behavior: 'smooth' })} className="hidden md:inline-block text-sm text-white/50 hover:text-white transition-colors">{label}</button>
            ))}
            <button data-testid="header-login-button" onClick={() => navigate('/login')} className="bg-[#C5A059] text-black px-8 py-3 text-sm font-medium tracking-wide hover:bg-[#E2C382] transition-colors">Login</button>
          </nav>
        </div>
      </header>

      {/* ─── HERO ─── */}
      <section className="relative pt-32 pb-16 md:pb-20 px-6 md:px-12 lg:px-24">
        <div className="absolute inset-0 z-0">
          <img src="https://images.unsplash.com/photo-1771226281605-f1e505ade901?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwyfHxkYXJrJTIwbHV4dXJ5JTIwYWJzdHJhY3QlMjB0ZXh0dXJlfGVufDB8fHx8MTc3NjA1OTE3MXww&ixlib=rb-4.1.0&q=85" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/75" />
        </div>
        <div className="relative z-10 max-w-5xl">
          <div className="text-xs uppercase tracking-[0.2em] text-[#C5A059] font-medium mb-6">Referral-Only Membership &middot; Launching in Gurgaon</div>
          <h1 className="text-5xl lg:text-7xl font-light tracking-tighter leading-tight text-white mb-4" style={{ fontFamily: 'Cormorant Garamond' }} data-testid="hero-heading">The Trusted Private Network</h1>
          <p className="text-base md:text-lg leading-relaxed text-white/60 font-light max-w-3xl mb-4">A curated ecosystem of trusted professionals, services, and relationships for people who value certainty over guesswork.</p>
          <p className="text-base text-white/40 font-light mb-10 max-w-2xl">We don't give you more options. We help you find the right one.</p>
          <div className="flex items-center gap-10">
            <button data-testid="hero-join-button" onClick={() => navigate('/register')} className="bg-[#C5A059] text-black px-12 py-4 text-sm font-medium tracking-wide hover:bg-[#E2C382] transition-colors shrink-0">Apply for Membership</button>
            <div className="hidden md:block">
              <div className="text-xs uppercase tracking-[0.15em] text-[#C5A059] font-medium mb-2">Why Members Join</div>
              <div className="grid grid-cols-2 gap-x-5 gap-y-1">
                <span className="text-white/50 text-sm"><span className="text-[#C5A059] mr-1">{'\u2713'}</span>No advertisements or paid ratings</span>
                <span className="text-white/50 text-sm"><span className="text-[#C5A059] mr-1">{'\u2713'}</span>Exclusive member benefits</span>
                <span className="text-white/50 text-sm"><span className="text-[#C5A059] mr-1">{'\u2713'}</span>Save time & avoid costly mistakes</span>
                <span className="text-white/50 text-sm"><span className="text-[#C5A059] mr-1">{'\u2713'}</span>Access trusted professionals</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ SECTION 2: WHY PRIVENET EXISTS ═══ */}
      <section id="why" className="py-10 md:py-14 px-6 md:px-12 lg:px-24 bg-[#0F0F14]" data-testid="why-aethel-section">
        <div className="max-w-4xl mx-auto">
          <div className="text-xs uppercase tracking-[0.2em] text-[#C5A059] font-medium mb-6 text-center">Why PriveNet Exists</div>
          <h2 className="text-3xl lg:text-4xl font-normal tracking-tight text-white/90 mb-8 text-center" style={{ fontFamily: 'Cormorant Garamond' }}>Most People Don't Need More Options</h2>
          <div className="max-w-2xl mx-auto text-center space-y-4 mb-10">
            <p className="text-white/60 text-sm leading-relaxed">When you need a CA, lawyer, doctor, contractor, or advisor, the real challenge isn't finding someone.</p>
            <p className="text-white text-lg font-light" style={{ fontFamily: 'Cormorant Garamond' }}>It's knowing who to trust.</p>
            <p className="text-white/60 text-sm leading-relaxed">One wrong choice can cost time, money, and peace of mind.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-white/[0.06] overflow-hidden max-w-3xl mx-auto" data-testid="comparison-table">
            <div className="p-5 bg-white/[0.02] border-b border-r border-white/[0.06]"><div className="text-white/40 text-xs uppercase tracking-wider">Typical Directory</div></div>
            <div className="p-5 bg-[#C5A059]/5 border-b border-white/[0.06]"><div className="text-[#C5A059] text-xs uppercase tracking-wider">PriveNet</div></div>
            {[
              ['Anonymous reviews', 'Personal referrals'],
              ['Paid placements', 'Merit-based inclusion'],
              ['Thousands of listings', 'Curated network'],
              ['Search & hope', 'Trusted recommendations'],
            ].map(([typical, aethel], i) => (
              <React.Fragment key={`cmp-${i}`}>
                <div className="p-4 border-b border-r border-white/[0.06] text-white/50 text-sm">{typical}</div>
                <div className="p-4 border-b border-white/[0.06] bg-[#C5A059]/5 text-white text-sm font-medium">{aethel}</div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ SECTION 3: TRUSTED CATEGORIES ═══ */}
      <section id="categories" className="py-10 md:py-14 px-6 md:px-12 lg:px-24" data-testid="categories-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-xs uppercase tracking-[0.2em] text-[#C5A059] font-medium mb-4 text-center">Launching with 20+ Verified Providers</div>
          <h2 className="text-3xl lg:text-4xl font-normal tracking-tight text-white/90 mb-4 text-center" style={{ fontFamily: 'Cormorant Garamond' }}>Experts Chosen By Reputation, Not Advertising.</h2>
          <p className="text-white/50 text-sm text-center max-w-2xl mx-auto mb-12">Every provider enters through referral, earns trust through performance, and stays through community feedback.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {categories.map((cat, i) => (
              <div key={`cat-${i}`} className={`${cat.featured ? 'bg-[#C5A059]/5 border-[#C5A059]/40' : 'bg-[#0F0F14] border-white/[0.04]'} border p-8 hover:border-[#C5A059]/30 transition-colors group relative`} data-testid={`landing-category-${i}`}>
                {cat.featured && <div className="absolute top-3 right-3 text-[10px] uppercase tracking-wider text-[#C5A059] font-medium bg-[#C5A059]/10 px-2 py-1">Featured</div>}
                <div className="text-3xl mb-4">{cat.icon}</div>
                <h3 className="text-lg font-medium text-white mb-2" style={{ fontFamily: 'Cormorant Garamond' }}>{cat.name}</h3>
                <p className="text-white/50 text-sm leading-relaxed">{cat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ SECTION 4: HOW IT WORKS ═══ */}
      <section id="how" className="py-10 md:py-14 px-6 md:px-12 lg:px-24 bg-[#0F0F14]" data-testid="how-it-works-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-xs uppercase tracking-[0.2em] text-[#C5A059] font-medium mb-6 text-center">How It Works</div>
          <h2 className="text-3xl lg:text-4xl font-normal tracking-tight text-white/90 mb-10 text-center" style={{ fontFamily: 'Cormorant Garamond' }}>Three Steps to Trusted Access</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-[#05050A] border border-white/[0.04] p-8 hover:border-white/[0.12] transition-colors">
              <div className="text-4xl text-[#C5A059] font-light mb-4" style={{ fontFamily: 'Cormorant Garamond' }}>01</div>
              <h3 className="text-lg font-medium text-white mb-3" style={{ fontFamily: 'Cormorant Garamond' }}>Get Invited</h3>
              <p className="text-white/60 text-sm leading-relaxed">Join through a member referral or request consideration for membership. Every member is personally vouched for.</p>
            </div>
            <div className="bg-[#05050A] border border-white/[0.04] p-8 hover:border-white/[0.12] transition-colors">
              <div className="text-4xl text-[#C5A059] font-light mb-4" style={{ fontFamily: 'Cormorant Garamond' }}>02</div>
              <h3 className="text-lg font-medium text-white mb-3" style={{ fontFamily: 'Cormorant Garamond' }}>Subscribe & Access</h3>
              <p className="text-white/60 text-sm leading-relaxed">Members at {'\u20B9'}299/mo and Providers at {'\u20B9'}799/mo gain access to trusted professionals. Avail membership discounts from verified providers.</p>
            </div>
            <div className="bg-[#05050A] border border-white/[0.04] p-8 hover:border-white/[0.12] transition-colors">
              <div className="text-4xl text-[#C5A059] font-light mb-4" style={{ fontFamily: 'Cormorant Garamond' }}>03</div>
              <h3 className="text-lg font-medium text-white mb-3" style={{ fontFamily: 'Cormorant Garamond' }}>Access with Confidence</h3>
              <p className="text-white/60 text-sm leading-relaxed">Connect directly with providers, submit feedback to keep quality of network, and grow the trusted community.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ SECTION 5: FOUNDER STORY ═══ */}
      <section className="py-10 md:py-14 px-6 md:px-12 lg:px-24" data-testid="founder-section">
        <div className="max-w-3xl mx-auto text-center">
          <div className="text-xs uppercase tracking-[0.2em] text-[#C5A059] font-medium mb-6">Founder Note</div>
          <h2 className="text-3xl lg:text-4xl font-normal tracking-tight text-white/90 mb-8" style={{ fontFamily: 'Cormorant Garamond' }}>Why I Built PriveNet</h2>
          <p className="text-white/60 text-sm leading-loose max-w-2xl mx-auto">After years of relying on referrals for important services, I realized that trust remains the most valuable currency. PriveNet was created to bring trusted relationships into a structured ecosystem.</p>
        </div>
      </section>

      {/* ═══ SECTION 6: WHY MEMBERS JOIN ═══ */}
      <section className="py-10 md:py-14 px-6 md:px-12 lg:px-24 bg-[#0F0F14]" data-testid="why-join-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-xs uppercase tracking-[0.2em] text-[#C5A059] font-medium mb-6 text-center">Why Members Join</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-[#05050A] border border-white/[0.04] p-8 text-center">
              <h3 className="text-xl font-medium text-white mb-3" style={{ fontFamily: 'Cormorant Garamond' }}>Save Time</h3>
              <p className="text-white/50 text-sm leading-relaxed">No more searching and comparing endlessly.</p>
            </div>
            <div className="bg-[#05050A] border border-white/[0.04] p-8 text-center">
              <h3 className="text-xl font-medium text-white mb-3" style={{ fontFamily: 'Cormorant Garamond' }}>Reduce Risk</h3>
              <p className="text-white/50 text-sm leading-relaxed">Every provider is selected through references.</p>
            </div>
            <div className="bg-[#05050A] border border-white/[0.04] p-8 text-center">
              <h3 className="text-xl font-medium text-white mb-3" style={{ fontFamily: 'Cormorant Garamond' }}>Gain Access</h3>
              <p className="text-white/50 text-sm leading-relaxed">Trusted professionals when you need them.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ SECTION 8: TRUST & PRICING ═══ */}
      <section id="pricing" className="py-10 md:py-14 px-6 md:px-12 lg:px-24 bg-[#0F0F14]" data-testid="pricing-section">
        <div className="max-w-4xl mx-auto text-center">
          <div className="text-xs uppercase tracking-[0.2em] text-[#C5A059] font-medium mb-6">Trust & Membership</div>
          <h2 className="text-3xl lg:text-4xl font-normal tracking-tight text-white/90 mb-3" style={{ fontFamily: 'Cormorant Garamond' }}>Founding Member Offer</h2>
          <p className="text-white/40 text-sm mb-2">Limited to our first 100 members.</p>
          <p className="text-white/30 text-xs mb-10">Reserved for early members who join before our official launch.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            <div className="bg-[#0F0F14] border border-white/[0.04] p-8 hover:border-[#C5A059]/30 transition-colors">
              <div className="text-xs uppercase tracking-wider text-[#C5A059] mb-4">Member</div>
              <div className="mb-3"><span className="text-4xl font-light text-white" style={{ fontFamily: 'Cormorant Garamond' }}>{'\u20B9'}299</span><span className="text-white/40 text-sm ml-2">/ month</span></div>
              <p className="text-white/40 text-sm mb-5">Access a trusted ecosystem of professionals, not just a directory.</p>
              <ul className="text-sm text-white/60 space-y-2 text-left mb-6">
                <li className="flex items-start gap-2"><span className="text-[#C5A059]">{'\u2713'}</span> Curated access to trusted professionals</li>
                <li className="flex items-start gap-2"><span className="text-[#C5A059]">{'\u2713'}</span> Personal recommendations based on your needs</li>
                <li className="flex items-start gap-2"><span className="text-[#C5A059]">{'\u2713'}</span> Exclusive partner benefits & priority access</li>
                <li className="flex items-start gap-2"><span className="text-[#C5A059]">{'\u2713'}</span> Direct WhatsApp connectivity with trusted partners</li>
                <li className="flex items-start gap-2"><span className="text-[#C5A059]">{'\u2713'}</span> Invite friends and earn member rewards</li>
              </ul>
              <button onClick={() => navigate('/register')} className="w-full bg-[#C5A059] text-black px-6 py-3 text-sm font-medium hover:bg-[#E2C382] transition-colors" data-testid="pricing-member-btn">Apply for Membership</button>
            </div>
            <div className="bg-[#0F0F14] border border-white/[0.04] p-8 hover:border-[#C5A059]/30 transition-colors">
              <div className="text-xs uppercase tracking-wider text-[#C5A059] mb-4">Service Provider</div>
              <div className="mb-3"><span className="text-4xl font-light text-white" style={{ fontFamily: 'Cormorant Garamond' }}>{'\u20B9'}799</span><span className="text-white/40 text-sm ml-2">/ month</span></div>
              <p className="text-white/40 text-sm mb-5">Join a curated network where reputation matters more than advertising.</p>
              <ul className="text-sm text-white/60 space-y-2 text-left mb-6">
                <li className="flex items-start gap-2"><span className="text-[#C5A059]">{'\u2713'}</span> Reach quality-conscious members</li>
                <li className="flex items-start gap-2"><span className="text-[#C5A059]">{'\u2713'}</span> Build trust through verified referrals</li>
                <li className="flex items-start gap-2"><span className="text-[#C5A059]">{'\u2713'}</span> No bidding or paid advertisements</li>
                <li className="flex items-start gap-2"><span className="text-[#C5A059]">{'\u2713'}</span> Premium partner profile & visibility</li>
                <li className="flex items-start gap-2"><span className="text-[#C5A059]">{'\u2713'}</span> Reputation-driven member recommendations</li>
                <li className="flex items-start gap-2"><span className="text-[#C5A059]">{'\u2713'}</span> Grow through long-term relationships</li>
              </ul>
              <button onClick={() => navigate('/register-provider')} className="w-full border border-[#C5A059] text-[#C5A059] px-6 py-3 text-sm font-medium hover:bg-[#C5A059] hover:text-black transition-colors" data-testid="pricing-provider-btn">Apply as a Service Provider</button>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ CONTACT US ═══ */}
      <section className="py-10 md:py-14 px-6 md:px-12 lg:px-24 bg-[#0F0F14]" data-testid="contact-section">
        <div className="max-w-xl mx-auto text-center">
          <div className="text-xs uppercase tracking-[0.2em] text-[#C5A059] font-medium mb-4">Contact Us</div>
          <h2 className="text-2xl lg:text-3xl font-normal tracking-tight text-white/90 mb-6" style={{ fontFamily: 'Cormorant Garamond' }}>Get in Touch</h2>
          <ContactForm />
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="py-12 px-6 md:px-12 lg:px-24 border-t border-white/[0.04]">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-xl font-light" style={{ fontFamily: 'Cormorant Garamond' }}>PriveNet</div>
            <div className="flex flex-wrap gap-6 text-sm text-white/40">
              <button onClick={() => navigate('/verify-membership')} className="hover:text-[#C5A059] transition-colors" data-testid="footer-verify-membership">Verify Member Status</button>
              <button onClick={() => navigate('/disclaimer')} className="hover:text-[#C5A059] transition-colors" data-testid="footer-disclaimer">Disclaimer</button>
              <button onClick={() => navigate('/faq')} className="hover:text-[#C5A059] transition-colors" data-testid="footer-faq">FAQ</button>
              <button onClick={() => navigate('/legal')} className="hover:text-[#C5A059] transition-colors" data-testid="footer-legal">Legal & Policies</button>
            </div>
            <div className="text-sm text-white/40">2026 PriveNet. Referral only.</div>
          </div>
        </div>
      </footer>
    </div>
  );
};

// ═══════════════════════════════════════════
// LOGIN PAGE
// ═══════════════════════════════════════════

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { login, user } = useAuth();

  useEffect(() => {
    if (user && user.role === 'admin') navigate('/admin');
    else if (user && user.role === 'service_provider') navigate('/provider-dashboard');
    else if (user) navigate('/dashboard');
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const result = await login(email, password);
    if (result.success) {
      if (result.data?.role === 'admin') navigate('/admin');
      else if (result.data?.role === 'service_provider') navigate('/provider-dashboard');
      else navigate('/dashboard');
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-[#05050A] flex flex-col" style={{ fontFamily: 'Outfit' }}>
      <header className="w-full px-6 md:px-12 lg:px-24 py-6">
        <button onClick={() => navigate('/')} className="text-2xl font-light text-white" style={{ fontFamily: 'Cormorant Garamond' }} data-testid="login-home-link">PriveNet</button>
      </header>
      <div className="flex-1 flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-light text-white mb-3" style={{ fontFamily: 'Cormorant Garamond' }} data-testid="login-heading">Welcome Back</h1>
          <p className="text-white/60 text-sm">Sign in to your exclusive account</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
          {error && <div className="p-4 bg-red-500/10 border border-red-500/20 text-red-400 text-sm" data-testid="login-error">{error}</div>}
          <GoldInput data-testid="login-email-input" label="Email" type="email" value={email} onChange={e => setEmail(e.target.value)} required />
          <GoldInput data-testid="login-password-input" label="Password" type="password" value={password} onChange={e => setPassword(e.target.value)} required />
          <GoldButton data-testid="login-submit-button" type="submit" className="w-full py-4">Sign In</GoldButton>
        </form>
        <div className="mt-8 text-center space-y-2">
          <p className="text-white/60 text-sm"><button data-testid="login-forgot-password" onClick={() => navigate('/forgot-password')} className="text-[#C5A059] hover:text-[#E2C382]">Forgot your password?</button></p>
          <p className="text-white/60 text-sm">Don't have an account? <button data-testid="login-to-register-link" onClick={() => navigate('/register')} className="text-[#C5A059] hover:text-[#E2C382]">Apply for Membership</button></p>
          <p className="text-white/60 text-sm">Service Provider? <button data-testid="login-to-sp-register" onClick={() => navigate('/register-provider')} className="text-[#C5A059] hover:text-[#E2C382]">Register as Provider</button></p>
        </div>
      </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════
// FORGOT PASSWORD PAGE
// ═══════════════════════════════════════════

const ForgotPasswordPage = () => {
  const [step, setStep] = useState(1); // 1=email, 2=otp, 3=new password
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setStep(2);
      setSuccess('A reset code has been sent to your email.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send reset code');
    }
    setLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (newPassword.length < 8) return setError('Password must be at least 8 characters');
    if (newPassword !== confirmPassword) return setError('Passwords do not match');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/reset-password`, { email, otp, new_password: newPassword });
      setSuccess(data.message);
      setStep(4);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#05050A] flex flex-col" style={{ fontFamily: 'Outfit' }}>
      <header className="w-full px-6 md:px-12 lg:px-24 py-6">
        <button onClick={() => navigate('/')} className="text-2xl font-light text-white" style={{ fontFamily: 'Cormorant Garamond' }} data-testid="forgot-home-link">PriveNet</button>
      </header>
      <div className="flex-1 flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-light text-white mb-3" style={{ fontFamily: 'Cormorant Garamond' }} data-testid="forgot-heading">
              {step === 4 ? 'Password Reset' : 'Reset Password'}
            </h1>
            <p className="text-white/60 text-sm">
              {step === 1 && 'Enter your email to receive a reset code'}
              {step === 2 && `Enter the 6-digit code sent to ${email}`}
              {step === 3 && 'Choose a new password'}
              {step === 4 && 'Redirecting you to sign in...'}
            </p>
          </div>

          {error && <div className="p-4 bg-red-500/10 border border-red-500/20 text-red-400 text-sm mb-6" data-testid="forgot-error">{error}</div>}
          {success && <div className="p-4 bg-green-500/10 border border-green-500/20 text-green-400 text-sm mb-6" data-testid="forgot-success">{success}</div>}

          {step === 1 && (
            <form onSubmit={handleSendOtp} className="space-y-6" data-testid="forgot-email-form">
              <GoldInput data-testid="forgot-email-input" label="Email" type="email" value={email} onChange={e => setEmail(e.target.value)} required />
              <GoldButton data-testid="forgot-send-code-btn" type="submit" className="w-full py-4" disabled={loading}>
                {loading ? 'Sending...' : 'Send Reset Code'}
              </GoldButton>
            </form>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <GoldInput data-testid="forgot-otp-input" label="Reset Code" type="text" value={otp} onChange={e => setOtp(e.target.value)} placeholder="Enter 6-digit code" maxLength={6} />
              <GoldButton data-testid="forgot-verify-otp-btn" className="w-full py-4" onClick={() => { if (otp.length === 6) { setStep(3); setError(''); setSuccess(''); } else { setError('Please enter the 6-digit code'); } }}>
                Verify Code
              </GoldButton>
              <button className="w-full text-center text-white/40 text-sm hover:text-white/60" onClick={() => { setError(''); setSuccess(''); handleSendOtp({ preventDefault: () => {} }); }} data-testid="forgot-resend-btn">Resend code</button>
            </div>
          )}

          {step === 3 && (
            <form onSubmit={handleResetPassword} className="space-y-6" data-testid="forgot-password-form">
              <GoldInput data-testid="forgot-new-password-input" label="New Password" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} required />
              <GoldInput data-testid="forgot-confirm-password-input" label="Confirm Password" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required />
              <GoldButton data-testid="forgot-reset-btn" type="submit" className="w-full py-4" disabled={loading}>
                {loading ? 'Resetting...' : 'Reset Password'}
              </GoldButton>
            </form>
          )}

          {step !== 4 && (
            <div className="mt-8 text-center">
              <button data-testid="forgot-back-to-login" onClick={() => navigate('/login')} className="text-white/60 text-sm hover:text-[#C5A059]">Back to Sign In</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════
// REGISTER PAGE
// ═══════════════════════════════════════════

const RegisterPage = () => {
  const [form, setForm] = useState({ name: '', email: '', password: '', referral_code: '', referral_type: 'member', city: '', state: '', phone: '', occupation: '', address_house: '', address_street: '', area_name: '', pin_code: '' });
  const [error, setError] = useState('');
  const [founderEnabled, setFounderEnabled] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [verifying, setVerifying] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register: registerUser, user } = useAuth();

  useEffect(() => { if (user) navigate('/dashboard'); }, [user, navigate]);
  useEffect(() => {
    axios.get(`${API}/settings/founder-invitation`).then(({ data }) => setFounderEnabled(data.enabled)).catch(() => {});
  }, []);
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      const type = searchParams.get('type');
      setForm(prev => ({ ...prev, referral_code: ref.toUpperCase(), referral_type: type === 'service_provider' ? 'service_provider' : 'member' }));
    }
  }, [searchParams]);

  const isFounder = form.referral_type === 'founder_invitation';

  const sendOtp = async () => {
    if (!form.email) return setError('Enter email first');
    setVerifying(true); setError('');
    try {
      await axios.post(`${API}/auth/send-verification`, { email: form.email });
      setOtpSent(true);
    } catch (err) { setError(formatError(err.response?.data?.detail)); }
    setVerifying(false);
  };

  const verifyOtp = async () => {
    setVerifying(true); setError('');
    try {
      await axios.post(`${API}/auth/verify-email`, { email: form.email, otp });
      setEmailVerified(true);
    } catch (err) { setError(formatError(err.response?.data?.detail)); }
    setVerifying(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!isValidEmail(form.email)) return setError('Please enter a valid email address.');
    if (!emailVerified) return setError('Please verify your email first.');
    if (!isFounder && !form.referral_code) return setError('Referral code is mandatory.');
    if (!form.city || !form.state || !form.phone) return setError('City, State and Phone are mandatory.');
    if (form.phone.length !== 10) return setError('Phone number must be 10 digits.');
    if (!form.occupation) return setError('Occupation is mandatory.');
    if (!form.address_house || !form.address_street) return setError('Address (House No./Building and Street Name) is mandatory.');
    if (!form.area_name) return setError('Sector/Landmark is mandatory.');
    if (form.pin_code.length !== 6) return setError('Pin Code must be 6 digits.');

    if (isFounder) {
      // Founder members get auto-approved with 3 months free
      const result = await registerUser({ ...form, referral_code: '' });
      if (result.success) {
        navigate('/dashboard');
      } else setError(result.error);
    } else {
      const result = await registerUser(form);
      if (result.success) navigate('/dashboard');
      else setError(result.error);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen bg-[#05050A] flex items-center justify-center px-6 py-16" style={{ fontFamily: 'Outfit' }}>
      <div className="w-full max-w-2xl">
        <div className="text-center mb-10">
          <h1 className="text-4xl font-light text-white mb-3" style={{ fontFamily: 'Cormorant Garamond' }} data-testid="register-heading">Apply for Membership</h1>
          <p className="text-white/60 text-sm">A valid referral code is required to join, or apply via Founder Invitation</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-5" data-testid="register-form">
          {error && <div className="p-4 bg-red-500/10 border border-red-500/20 text-red-400 text-sm" data-testid="register-error">{error}</div>}

          <div className="bg-[#0F0F14] border border-[#C5A059]/30 p-6">
            <div className="mb-4">
              <label className="block text-sm text-white/60 mb-2">Referral Type <span className="text-[#C5A059]">*</span></label>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 text-white/80 text-sm cursor-pointer">
                  <input type="radio" name="referral_type" value="member" checked={form.referral_type === 'member'} onChange={set('referral_type')} className="accent-[#C5A059]" data-testid="referral-type-member" /> Member Referral
                </label>
                <label className="flex items-center gap-2 text-white/80 text-sm cursor-pointer">
                  <input type="radio" name="referral_type" value="service_provider" checked={form.referral_type === 'service_provider'} onChange={set('referral_type')} className="accent-[#C5A059]" data-testid="referral-type-provider" /> Service Provider Referral
                </label>
                {founderEnabled && (
                  <label className="flex items-center gap-2 text-white/80 text-sm cursor-pointer">
                    <input type="radio" name="referral_type" value="founder_invitation" checked={form.referral_type === 'founder_invitation'} onChange={set('referral_type')} className="accent-[#C5A059]" data-testid="referral-type-founder" /> Founder Invitation
                  </label>
                )}
              </div>
            </div>
            {!isFounder && (
              <GoldInput data-testid="register-referral-input" label="Referral Code (Membership ID)" value={form.referral_code} onChange={set('referral_code')} required placeholder="Enter your referral code" />
            )}
            {isFounder && (
              <div className="p-3 bg-[#C5A059]/5 border border-[#C5A059]/20 text-sm text-white/60">
                No referral code needed. You'll get 3 months free access as a founding member.
              </div>
            )}
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <GoldInput data-testid="register-name-input" label="Full Name" value={form.name} onChange={set('name')} required />
            <div>
              <GoldInput data-testid="register-email-input" label="Email" type="email" value={form.email} onChange={set('email')} required disabled={emailVerified} />
              {!emailVerified && !otpSent && (
                <button type="button" data-testid="send-otp-btn" onClick={sendOtp} disabled={verifying} className="mt-2 text-[#C5A059] text-xs hover:text-[#E2C382] disabled:opacity-50">{verifying ? 'Sending...' : 'Verify Email'}</button>
              )}
              {otpSent && !emailVerified && (
                <>
                  <div className="flex gap-2 mt-2">
                    <input data-testid="otp-input" value={otp} onChange={e => setOtp(e.target.value)} placeholder="Enter 6-digit code" className="flex-1 bg-transparent border-b border-white/20 py-1 text-white text-sm focus:border-[#C5A059] focus:outline-none" maxLength={6} />
                    <button type="button" data-testid="verify-otp-btn" onClick={verifyOtp} disabled={verifying} className="text-[#C5A059] text-xs hover:text-[#E2C382] disabled:opacity-50">{verifying ? 'Verifying...' : 'Confirm'}</button>
                  </div>
                  <button type="button" data-testid="resend-otp-btn" onClick={() => { setOtp(''); sendOtp(); }} disabled={verifying} className="mt-2 text-white/50 text-xs hover:text-[#C5A059] disabled:opacity-50 underline underline-offset-2">Wrong email or didn't get the code? Edit email above & Resend Code</button>
                </>
              )}
              {emailVerified && <span className="text-green-400 text-xs mt-1 block">{'\u2713'} Email verified</span>}
            </div>
          </div>
          <GoldInput data-testid="register-password-input" label="Password" type="password" value={form.password} onChange={set('password')} required />
          <div>
            <label className="block text-sm text-white/60 mb-2">Address <span className="text-[#C5A059]">*</span></label>
            <div className="grid md:grid-cols-2 gap-5">
              <input data-testid="register-house-input" value={form.address_house} onChange={set('address_house')} required placeholder="House No. / Building" className="w-full bg-transparent border-b border-white/20 py-3 text-white focus:border-[#C5A059] focus:outline-none transition-colors placeholder:text-white/20" />
              <input data-testid="register-street-input" value={form.address_street} onChange={set('address_street')} required placeholder="Street Name" className="w-full bg-transparent border-b border-white/20 py-3 text-white focus:border-[#C5A059] focus:outline-none transition-colors placeholder:text-white/20" />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <GoldInput data-testid="register-area-input" label="Sector/Landmark" value={form.area_name} onChange={set('area_name')} required placeholder="e.g. Sector 45, Near Metro" />
            <div>
              <GoldInput data-testid="register-pincode-input" label="Pin Code" type="tel" value={form.pin_code} onChange={e => { const v = e.target.value.replace(/\D/g, '').slice(0, 6); setForm(prev => ({ ...prev, pin_code: v })); if (v.length === 6) lookupPincode(v).then(r => { if (r) setForm(prev => ({ ...prev, city: r.city, state: r.state })); }); }} required maxLength={6} placeholder="122001" />
              {form.pin_code && form.pin_code.length !== 6 && <span className="text-red-400 text-xs">Must be 6 digits</span>}
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm text-white/60 mb-2">City <span className="text-[#C5A059]">*</span></label>
              <select data-testid="register-city-input" value={form.city} onChange={e => { const c = e.target.value; setForm(prev => ({ ...prev, city: c, state: CITY_STATE_MAP[c] || prev.state })); }} required className="w-full bg-transparent border border-white/20 py-3 px-3 text-white text-sm focus:border-[#C5A059] focus:outline-none">
                <option value="" className="bg-[#0F0F14]">Select City</option>
                {(STATE_CITIES_MAP[form.state] || MAJOR_CITIES).map(c => <option key={c} value={c} className="bg-[#0F0F14]">{c}</option>)}
                {form.city && form.city !== '__other__' && !(STATE_CITIES_MAP[form.state] || MAJOR_CITIES).includes(form.city) && <option value={form.city} className="bg-[#0F0F14]">{form.city}</option>}
                <option value="__other__" className="bg-[#0F0F14]">Other</option>
              </select>
              {form.city === '__other__' && (
                <input data-testid="register-city-other" onChange={e => setForm(prev => ({ ...prev, city: e.target.value }))} placeholder="Enter your city" className="w-full mt-2 bg-transparent border-b border-white/20 py-2 text-white text-sm focus:border-[#C5A059] focus:outline-none placeholder:text-white/20" />
              )}
            </div>
            <div>
              <label className="block text-sm text-white/60 mb-2">State <span className="text-[#C5A059]">*</span></label>
              <select data-testid="register-state-input" value={form.state} onChange={set('state')} required className="w-full bg-transparent border border-white/20 py-3 px-3 text-white text-sm focus:border-[#C5A059] focus:outline-none">
                <option value="" className="bg-[#0F0F14]">Select State</option>
                {INDIAN_STATES.map(s => <option key={s} value={s} className="bg-[#0F0F14]">{s}</option>)}
              </select>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <GoldInput data-testid="register-phone-input" label="Phone (10 digits)" type="tel" value={form.phone} onChange={e => { const v = e.target.value.replace(/\D/g, '').slice(0, 10); setForm({ ...form, phone: v }); }} required maxLength={10} placeholder="9876543210" />
              {form.phone && form.phone.length !== 10 && <span className="text-red-400 text-xs">Must be 10 digits</span>}
            </div>
            <GoldInput data-testid="register-occupation-input" label="Occupation" value={form.occupation} onChange={set('occupation')} required />
          </div>
          <GoldButton data-testid="register-submit-button" type="submit" className="w-full py-4">Submit Application</GoldButton>
        </form>
        <div className="mt-6 text-center">
          <p className="text-white/60 text-sm">Already a member? <button data-testid="register-to-login-link" onClick={() => navigate('/login')} className="text-[#C5A059] hover:text-[#E2C382]">Sign In</button></p>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════
// MEMBER DASHBOARD
// ═══════════════════════════════════════════

const DashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [subData, setSubData] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [showReqForm, setShowReqForm] = useState(false);
  const [reqForm, setReqForm] = useState({ category: '', description: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [reqStatus, setReqStatus] = useState('');

  const [quickCats, setQuickCats] = useState([]);

  useEffect(() => {
    axios.get(`${API}/my-subscription`, { withCredentials: true }).then(({ data }) => setSubData(data)).catch(() => {});
    axios.get(`${API}/community-updates`, { withCredentials: true }).then(({ data }) => setUpdates(data)).catch(() => {});
    axios.get(`${API}/categories`, { withCredentials: true }).then(({ data }) => setQuickCats((data || []).slice(0, 6))).catch(() => {});
  }, [user]);

  const handleReqSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/recommendation-requests`, reqForm, { withCredentials: true });
      setReqStatus('Request submitted! We will match you with a trusted professional.');
      setReqForm({ category: '', description: '' });
      setTimeout(() => { setShowReqForm(false); setReqStatus(''); }, 3000);
    } catch (err) { setReqStatus('Failed to submit. Please try again.'); }
  };

  const catIcon = (name) => {
    const n = (name || '').toLowerCase();
    if (/home|real estate|interior|repair|maintenance/.test(n)) return Home;
    if (/legal|law/.test(n)) return Scale;
    if (/finance|tax|account|invest/.test(n)) return PieChart;
    if (/health|wellness|medical|fitness/.test(n)) return HeartPulse;
    if (/business|corporate|consult/.test(n)) return Briefcase;
    if (/spiritual|growth|astro|pooja/.test(n)) return Sparkles;
    if (/service|plumb|electric|handy/.test(n)) return Wrench;
    if (/educat|tutor|coach|learn/.test(n)) return GraduationCap;
    if (/auto|car|vehicle|transport/.test(n)) return Car;
    if (/food|cater|restaurant/.test(n)) return UtensilsCrossed;
    if (/insur|security|protect/.test(n)) return Shield;
    return LayoutGrid;
  };

  const isActive = subData?.has_subscription;

  return (
    <div className="min-h-screen bg-[#05050A]" style={{ fontFamily: 'Outfit' }}>
      <Header />
      <main className="pt-24 px-6 md:px-12 lg:px-24 py-8">

        {/* Welcome + Status */}
        <div className="mb-8" data-testid="dashboard-welcome">
          <h1 className="text-3xl lg:text-4xl font-light text-white mb-1" style={{ fontFamily: 'Cormorant Garamond' }}>Welcome, {user?.name}</h1>
          <p className="text-white/40 text-sm">Your Membership Benefits</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-[#0F0F14] border border-white/[0.04] px-6 py-4 flex items-center gap-3" data-testid="status-member">
            <span className={`w-2.5 h-2.5 rounded-full ${isActive ? 'bg-green-400' : 'bg-yellow-400'}`}></span>
            <span className="text-white/50 text-sm">Membership:</span>
            <span className="text-white text-sm font-medium">{isActive ? 'Active' : 'Pending'}</span>
          </div>
          <div className="bg-[#0F0F14] border border-white/[0.04] px-6 py-4 flex items-center gap-3" data-testid="status-priority">
            <span className="text-[#C5A059]">{'\u2B50'}</span>
            <span className="text-white/50 text-sm">Priority Access:</span>
            <span className="text-white text-sm font-medium">Enabled</span>
          </div>
          <div className="bg-[#0F0F14] border border-white/[0.04] px-6 py-4 flex items-center gap-3" data-testid="status-benefits">
            <span className="text-[#C5A059]">{'\uD83C\uDF81'}</span>
            <span className="text-white/50 text-sm">Partner Benefits:</span>
            <span className="text-white text-sm font-medium">Available</span>
          </div>
        </div>

        {/* Expert Search Box */}
        <div className="bg-[#0F0F14] border border-white/[0.08] p-8 mb-8" data-testid="dashboard-search-box">
          <h2 className="text-xl font-light text-white mb-2" style={{ fontFamily: 'Cormorant Garamond' }}>Looking for a trusted professional?</h2>
          <p className="text-white/40 text-sm mb-5">Search by service, profession or describe your requirement</p>
          <div className="flex gap-3">
            <input data-testid="dashboard-search-input" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') navigate(searchQuery.trim() ? `/services?search=${encodeURIComponent(searchQuery.trim())}` : '/services'); }}
              placeholder="e.g. Tax Consultant, Interior Designer, Wellness Coach..."
              className="flex-1 bg-transparent border border-white/20 py-3.5 px-5 text-white text-sm focus:border-[#C5A059] focus:outline-none placeholder:text-white/25 transition-colors" />
            <button data-testid="dashboard-search-btn" onClick={() => navigate(searchQuery.trim() ? `/services?search=${encodeURIComponent(searchQuery.trim())}` : '/services')}
              className="bg-[#C5A059] text-black px-8 py-3.5 text-sm font-medium tracking-wide hover:bg-[#E2C382] transition-colors whitespace-nowrap">
              Find My Expert
            </button>
          </div>
        </div>

        {/* Need Something Today? — Category Quick Access */}
        <div className="mb-8">
          <h2 className="text-lg font-medium text-white/80 mb-4" style={{ fontFamily: 'Cormorant Garamond' }}>Or browse by category</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {quickCats.map((cat, i) => {
              const highlight = /health/i.test(cat.name);
              const Icon = catIcon(cat.name);
              return (
                <button key={cat.id} data-testid={`quick-cat-${i}`} onClick={() => navigate(`/services?cat=${cat.id}`)} className={`p-5 text-center transition-colors group ${highlight ? 'bg-[#C5A059]/10 border border-[#C5A059]/40 hover:border-[#C5A059]' : 'bg-[#0F0F14] border border-white/[0.04] hover:border-[#C5A059]/30'}`}>
                  <Icon className={`w-6 h-6 mx-auto mb-2 ${highlight ? 'text-[#C5A059]' : 'text-white/60 group-hover:text-[#C5A059]'} transition-colors`} strokeWidth={1.5} />
                  <div className={`text-xs font-medium group-hover:text-white transition-colors ${highlight ? 'text-[#C5A059]' : 'text-white/70'}`}>{cat.name}</div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Member Benefits */}
          <div className="bg-[#0F0F14] border border-white/[0.04] p-6" data-testid="member-benefits-card">
            <h3 className="text-lg font-medium text-white mb-4" style={{ fontFamily: 'Cormorant Garamond' }}>Your Member Benefits</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-sm"><span className="text-[#C5A059]">{'\u2713'}</span><span className="text-white/70">Priority access to verified professionals</span></div>
              <div className="flex items-center gap-3 text-sm"><span className="text-[#C5A059]">{'\u2713'}</span><span className="text-white/70">Exclusive benefits from trusted partners</span></div>
              <div className="flex items-center gap-3 text-sm"><span className="text-[#C5A059]">{'\u2713'}</span><span className="text-white/70">Direct WhatsApp contact with providers</span></div>
              <div className="flex items-center gap-3 text-sm"><span className="text-[#C5A059]">{'\u2713'}</span><span className="text-white/70">Community updates & exclusive access</span></div>
            </div>
            {!isActive && (
              <button onClick={() => navigate('/subscription')} className="mt-4 w-full bg-[#C5A059] text-black py-3 text-sm font-medium hover:bg-[#E2C382] transition-colors" data-testid="activate-sub-btn">Activate Subscription</button>
            )}
          </div>

          {/* Community Updates */}
          <div className="bg-[#0F0F14] border border-white/[0.04] p-6" data-testid="community-updates-card">
            <h3 className="text-lg font-medium text-white mb-4" style={{ fontFamily: 'Cormorant Garamond' }}>Community Updates</h3>
            {updates.length === 0 ? (
              <div className="space-y-3">
                <p className="text-[#C5A059] text-sm font-medium">Welcome to the Founding Community</p>
                <p className="text-white/50 text-sm">You're among our early members.</p>
                <p className="text-white/40 text-sm">We're currently onboarding trusted partners across Gurgaon and adding new member benefits regularly.</p>
                <p className="text-white/50 text-sm italic">Stay tuned for exclusive updates.</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-52 overflow-y-auto">
                {updates.map(u => (
                  <div key={u.id} className="border-b border-white/[0.04] pb-3 last:border-0">
                    <div className="text-white text-sm font-medium">{u.title}</div>
                    <div className="text-white/50 text-xs mt-1">{u.content}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Quick Links Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button data-testid="dashboard-partners-card" onClick={() => navigate('/services')} className="bg-[#0F0F14] border border-white/[0.04] p-5 hover:border-[#C5A059]/30 transition-colors text-left">
            <h3 className="text-base font-medium text-white mb-1" style={{ fontFamily: 'Cormorant Garamond' }}>Browse Trusted Partners</h3>
            <p className="text-white/40 text-xs">Explore the full provider directory</p>
          </button>
          <button data-testid="dashboard-invite-card" onClick={() => navigate('/referrals')} className="bg-[#0F0F14] border border-white/[0.04] p-5 hover:border-[#C5A059]/30 transition-colors text-left">
            <h3 className="text-base font-medium text-white mb-1" style={{ fontFamily: 'Cormorant Garamond' }}>Invite Friends</h3>
            <p className="text-white/40 text-xs">Share your referral code</p>
          </button>
          <button data-testid="dashboard-subscription-card" onClick={() => navigate('/subscription')} className="bg-[#0F0F14] border border-white/[0.04] p-5 hover:border-[#C5A059]/30 transition-colors text-left">
            <h3 className="text-base font-medium text-white mb-1" style={{ fontFamily: 'Cormorant Garamond' }}>Membership</h3>
            <p className="text-white/40 text-xs">Manage subscription & payments</p>
          </button>
        </div>

        {/* Contact Us Footer */}
        <div className="mt-12 pt-6 border-t border-white/[0.06] text-center">
          <p className="text-white/40 text-sm">Need help? Contact us at <a href="mailto:support@privenet.in" className="text-[#C5A059] hover:text-[#E2C382]">support@privenet.in</a></p>
        </div>

      </main>
    </div>
  );
};

// ═══════════════════════════════════════════
// REFERRALS PAGE
// ═══════════════════════════════════════════

const ReferralsPage = () => {
  const [stats, setStats] = useState(null);
  const [copied, setCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    axios.get(`${API}/referrals/stats`, { withCredentials: true })
      .then(({ data }) => setStats(data))
      .catch(() => {});
  }, []);

  const copyCode = () => {
    if (stats?.referral_code) {
      navigator.clipboard.writeText(stats.referral_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const inviteLink = `${window.location.origin}/register?ref=${stats?.referral_code || ''}&type=member`;
  const inviteMsg = `You've been invited to PriveNet — The Trusted Private Network.\n\nPriveNet is an exclusive, referral-only community of verified professionals and trusted service providers. Members get priority access, member discounts, and direct connections to curated experts across legal, finance, health, home services, and more.\n\nUse my referral code to apply:\n\nReferral Code: ${stats?.referral_code || ''}\n\nApply here (code auto-filled): ${inviteLink}\n\nSent by ${user?.name || 'a PriveNet member'}`;

  const copyLink = () => {
    navigator.clipboard.writeText(inviteLink);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const shareWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(inviteMsg)}`, '_blank');
  };

  const shareEmail = () => {
    const subject = `You're Invited to PriveNet — The Trusted Private Network`;
    window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(inviteMsg)}`, '_blank');
  };

  const copyInvite = () => {
    navigator.clipboard.writeText(inviteMsg);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-[#05050A]" style={{ fontFamily: 'Outfit' }}>
      <Header />
      <main className="pt-24 px-6 md:px-12 lg:px-24 py-12">
        <h1 className="text-4xl lg:text-5xl font-light text-white mb-3" style={{ fontFamily: 'Cormorant Garamond' }} data-testid="referrals-heading">Invite Friends</h1>
        <p className="text-white/60 text-sm mb-8">Share your referral code to invite trusted members or service providers</p>

        {stats && (
          <div className="space-y-6">
            <div className="bg-[#0F0F14] border border-white/[0.04] p-8" data-testid="referral-code-section">
              <div className="text-xs uppercase tracking-[0.2em] text-[#C5A059] font-medium mb-4">Your Referral Code (Member ID)</div>
              <div className="flex items-center gap-4 flex-wrap mb-6">
                <div className="text-3xl font-light text-white" style={{ fontFamily: 'Cormorant Garamond' }} data-testid="referral-code">{stats.referral_code}</div>
                <GoldButton data-testid="copy-referral-button" onClick={copyCode}>{copied ? 'Copied!' : 'Copy Code'}</GoldButton>
                <GoldButton variant="secondary" data-testid="copy-invite-link-button" onClick={copyLink}>{linkCopied ? 'Link Copied!' : 'Copy Invite Link'}</GoldButton>
              </div>
              <p className="text-white/40 text-xs -mt-3 mb-6" data-testid="invite-link-preview">Invite link (code auto-filled): <span className="text-[#C5A059]/80 break-all">{inviteLink}</span></p>

              {/* Share Invite */}
              <div className="border-t border-white/[0.06] pt-5">
                <div className="text-xs uppercase tracking-[0.15em] text-white/40 mb-3">Share Invite</div>
                <div className="flex flex-wrap gap-3">
                  <button data-testid="share-whatsapp" onClick={shareWhatsApp} className="flex items-center gap-2 bg-[#25D366] text-white px-5 py-2.5 text-sm font-medium hover:bg-[#1da851] transition-colors">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    Share via WhatsApp
                  </button>
                  <button data-testid="share-email" onClick={shareEmail} className="flex items-center gap-2 bg-white/10 text-white px-5 py-2.5 text-sm font-medium hover:bg-white/20 transition-colors">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
                    Share via Email
                  </button>
                  <button data-testid="copy-invite-msg" onClick={copyInvite} className="flex items-center gap-2 border border-white/20 text-white/70 px-5 py-2.5 text-sm font-medium hover:border-white/40 transition-colors">
                    Copy Full Invite
                  </button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-[#0F0F14] border border-white/[0.04] p-8" data-testid="member-referrals-section">
                <div className="text-xs uppercase tracking-[0.2em] text-[#C5A059] font-medium mb-4">Member Referrals</div>
                <div className="text-4xl font-light text-white mb-4" style={{ fontFamily: 'Cormorant Garamond' }}>{stats.member_referrals?.length || 0}</div>
                {stats.member_referrals?.map((r, i) => (
                  <div key={i} className="flex justify-between py-2 border-b border-white/[0.04] text-sm" data-testid={`member-referral-${i}`}>
                    <div><div className="text-white">{r.name}</div><div className="text-white/40 text-xs">{r.email} {r.city ? `- ${r.city}` : ''}</div></div>
                    <div className="text-white/40 text-xs">{r.created_at ? new Date(r.created_at).toLocaleDateString() : ''}</div>
                  </div>
                ))}
              </div>
              <div className="bg-[#0F0F14] border border-white/[0.04] p-8" data-testid="provider-referrals-section">
                <div className="text-xs uppercase tracking-[0.2em] text-[#C5A059] font-medium mb-4">Service Provider Referrals</div>
                <div className="text-4xl font-light text-white mb-4" style={{ fontFamily: 'Cormorant Garamond' }}>{stats.provider_referrals?.length || 0}</div>
                {stats.provider_referrals?.map((r, i) => (
                  <div key={i} className="flex justify-between py-2 border-b border-white/[0.04] text-sm" data-testid={`provider-referral-${i}`}>
                    <div><div className="text-white">{r.name}</div><div className="text-white/40 text-xs">{r.email}</div></div>
                    <div className="text-white/40 text-xs">{r.created_at ? new Date(r.created_at).toLocaleDateString() : ''}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

// ═══════════════════════════════════════════
// PROFILE PAGE
// ═══════════════════════════════════════════

const ProfilePage = () => {
  const { user, checkAuth } = useAuth();
  const [form, setForm] = useState({ name: '', city: '', state: '', phone: '', bio: '' });
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [profileMsg, setProfileMsg] = useState('');
  const [pwMsg, setPwMsg] = useState('');
  const [pwError, setPwError] = useState('');

  useEffect(() => {
    if (user) {
      setForm({ name: user.name || '', city: user.city || '', state: user.state || '', phone: user.phone || '', bio: user.bio || '' });
    }
  }, [user]);

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setProfileMsg('');
    try {
      await axios.put(`${API}/profile`, form, { withCredentials: true });
      setProfileMsg('Profile updated successfully!');
      await checkAuth();
      setTimeout(() => setProfileMsg(''), 3000);
    } catch (err) {
      setProfileMsg(formatError(err.response?.data?.detail));
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPwMsg(''); setPwError('');
    if (pwForm.new_password !== pwForm.confirm_password) {
      setPwError('New passwords do not match');
      return;
    }
    try {
      await axios.post(`${API}/profile/change-password`, { current_password: pwForm.current_password, new_password: pwForm.new_password }, { withCredentials: true });
      setPwMsg('Password changed successfully!');
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
      setTimeout(() => setPwMsg(''), 3000);
    } catch (err) {
      setPwError(formatError(err.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-screen bg-[#05050A]" style={{ fontFamily: 'Outfit' }}>
      <Header />
      <main className="pt-24 px-6 md:px-12 lg:px-24 py-12 max-w-4xl">
        <h1 className="text-4xl lg:text-5xl font-light text-white mb-8" style={{ fontFamily: 'Cormorant Garamond' }} data-testid="profile-heading">My Profile</h1>

        {/* Profile Update */}
        <form onSubmit={handleProfileUpdate} className="bg-[#0F0F14] border border-white/[0.04] p-8 mb-8 space-y-5" data-testid="profile-form">
          <h3 className="text-xl font-light text-white/80 mb-2" style={{ fontFamily: 'Cormorant Garamond' }}>Personal Details</h3>
          {profileMsg && <div className="p-3 bg-[#C5A059]/10 text-[#C5A059] text-sm" data-testid="profile-message">{profileMsg}</div>}
          <div className="grid md:grid-cols-2 gap-5">
            <GoldInput data-testid="profile-name" label="Name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required />
            <GoldInput data-testid="profile-phone" label="Phone" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} />
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <GoldInput data-testid="profile-city" label="City" value={form.city} onChange={e => setForm({...form, city: e.target.value})} />
            <GoldInput data-testid="profile-state" label="State" value={form.state} onChange={e => setForm({...form, state: e.target.value})} />
          </div>
          <div>
            <label className="block text-sm text-white/60 mb-2">Bio</label>
            <textarea data-testid="profile-bio" value={form.bio} onChange={e => setForm({...form, bio: e.target.value})} className="w-full bg-transparent border border-white/20 py-3 px-4 text-white focus:border-[#C5A059] focus:outline-none h-20 resize-none" />
          </div>
          <GoldButton data-testid="profile-save-btn" type="submit">Save Changes</GoldButton>
        </form>

        {/* Change Password */}
        <form onSubmit={handlePasswordChange} className="bg-[#0F0F14] border border-white/[0.04] p-8 space-y-5" data-testid="password-form">
          <h3 className="text-xl font-light text-white/80 mb-2" style={{ fontFamily: 'Cormorant Garamond' }}>Change Password</h3>
          {pwMsg && <div className="p-3 bg-green-500/10 text-green-400 text-sm" data-testid="password-success">{pwMsg}</div>}
          {pwError && <div className="p-3 bg-red-500/10 border border-red-500/20 text-red-400 text-sm" data-testid="password-error">{pwError}</div>}
          <GoldInput data-testid="current-password" label="Current Password" type="password" value={pwForm.current_password} onChange={e => setPwForm({...pwForm, current_password: e.target.value})} required />
          <div className="grid md:grid-cols-2 gap-5">
            <GoldInput data-testid="new-password" label="New Password" type="password" value={pwForm.new_password} onChange={e => setPwForm({...pwForm, new_password: e.target.value})} required />
            <GoldInput data-testid="confirm-password" label="Confirm New Password" type="password" value={pwForm.confirm_password} onChange={e => setPwForm({...pwForm, confirm_password: e.target.value})} required />
          </div>
          <GoldButton data-testid="change-password-btn" type="submit">Change Password</GoldButton>
        </form>
      </main>
    </div>
  );
};

// ═══════════════════════════════════════════
// SUBSCRIPTION PAGE (Member-facing)
// ═══════════════════════════════════════════

const SubscriptionPage = () => {
  const [subData, setSubData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const { user, checkAuth } = useAuth();

  const loadSub = useCallback(() => {
    axios.get(`${API}/my-subscription`, { withCredentials: true })
      .then(({ data }) => setSubData(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadSub(); }, [loadSub]);

  const handlePayNow = async () => {
    setPaying(true);
    try {
      const subType = user?.role === 'service_provider' ? 'provider' : 'member';
      const { data } = await axios.post(`${API}/payments/razorpay/create-order`, { sub_type: subType }, { withCredentials: true });

      const options = {
        key: data.key_id,
        subscription_id: data.subscription_id,
        name: data.name,
        description: data.description,
        prefill: data.prefill,
        theme: { color: '#C5A059' },
        handler: async (response) => {
          try {
            await axios.post(`${API}/payments/razorpay/verify`, {
              razorpay_subscription_id: response.razorpay_subscription_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              internal_sub_id: data.internal_sub_id,
            }, { withCredentials: true });
            await checkAuth();
            loadSub();
          } catch (err) {
            alert('Payment verification failed. Please contact support.');
          }
        },
        modal: { ondismiss: () => setPaying(false) }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create subscription');
      setPaying(false);
    }
  };

  const amount = user?.role === 'service_provider' ? 799 : 299;
  const planLabel = user?.role === 'service_provider' ? 'Service Provider Plan' : 'Member Plan';

  return (
    <div className="min-h-screen bg-[#05050A]" style={{ fontFamily: 'Outfit' }}>
      <Header />
      <main className="pt-24 px-6 md:px-12 lg:px-24 py-12 max-w-3xl">
        <h1 className="text-4xl lg:text-5xl font-light text-white mb-8" style={{ fontFamily: 'Cormorant Garamond' }} data-testid="subscription-heading">My Subscription</h1>

        {loading ? <p className="text-white/60">Loading...</p> : subData && (
          <div className="space-y-6">
            <div className="bg-[#0F0F14] border border-white/[0.04] p-8" data-testid="subscription-status">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-[#C5A059] font-medium mb-2">{planLabel}</div>
                  <div className="text-3xl font-light text-white" style={{ fontFamily: 'Cormorant Garamond' }}>
                    {'\u20B9'}{amount}<span className="text-white/40 text-sm ml-2">/ month</span>
                  </div>
                </div>
                <span className={`text-sm px-4 py-2 ${subData.has_subscription ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`} data-testid="subscription-badge">
                  {subData.has_subscription ? 'Active' : 'Inactive'}
                </span>
              </div>

              {subData.has_subscription ? (
                <div className="border-t border-white/[0.04] pt-6">
                  <p className="text-white/60 text-sm">Your subscription is active. Auto-renews monthly via Razorpay.</p>
                </div>
              ) : (
                <div className="border-t border-white/[0.04] pt-6">
                  <p className="text-white/60 text-sm mb-6">Your subscription is not active. Subscribe now to unlock full access.</p>
                  <button data-testid="pay-now-btn" onClick={handlePayNow} disabled={paying} className="w-full bg-[#C5A059] text-black px-8 py-4 text-sm font-medium tracking-wide hover:bg-[#E2C382] transition-colors disabled:opacity-50">
                    {paying ? 'Processing...' : `Subscribe ${'\u20B9'}${amount}/month`}
                  </button>
                  <p className="text-white/30 text-xs mt-3 text-center">Secure payment via Razorpay. Cancel anytime.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

// ─── Admin Founder Requests ───
const AdminFounderRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/admin/founder-requests`, { withCredentials: true })
      .then(({ data }) => setRequests(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAction = async (userId, action) => {
    try {
      await axios.post(`${API}/admin/founder-requests/${userId}/${action}`, {}, { withCredentials: true });
      load();
    } catch (err) { console.error('Founder request action failed:', err.message); }
  };

  if (loading) return <p className="text-white/60">Loading...</p>;

  return (
    <div data-testid="admin-founder-requests">
      <h2 className="text-2xl font-light text-white mb-6" style={{ fontFamily: 'Cormorant Garamond' }}>Founder Invitation Requests</h2>
      {requests.length === 0 ? (
        <p className="text-white/40 text-sm">No pending founder requests.</p>
      ) : (
        <div className="space-y-4">
          {requests.map(r => (
            <div key={r.id} className="bg-[#0F0F14] border border-white/[0.04] p-6 flex flex-col md:flex-row md:items-center justify-between gap-4" data-testid={`founder-request-${r.id}`}>
              <div>
                <div className="text-white font-medium">{r.name}</div>
                <div className="text-white/50 text-sm">{r.email} &middot; {r.city}, {r.state}</div>
                <div className="text-white/40 text-xs mt-1">{r.occupation} &middot; {r.area_name}</div>
                {r.bio && <div className="text-white/30 text-xs mt-1 italic">"{r.bio}"</div>}
              </div>
              <div className="flex gap-3">
                <button data-testid={`founder-approve-${r.id}`} onClick={() => handleAction(r.id, 'approve')} className="bg-green-600/80 text-white px-5 py-2 text-sm hover:bg-green-600 transition-colors">Approve</button>
                <button data-testid={`founder-reject-${r.id}`} onClick={() => handleAction(r.id, 'reject')} className="bg-red-600/80 text-white px-5 py-2 text-sm hover:bg-red-600 transition-colors">Reject</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Admin Settings ───
const AdminSettings = () => {
  const [founderEnabled, setFounderEnabled] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/settings/founder-invitation`, { withCredentials: true })
      .then(({ data }) => setFounderEnabled(data.enabled))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const toggle = async () => {
    const newVal = !founderEnabled;
    try {
      await axios.put(`${API}/admin/settings/founder-invitation`, { enabled: newVal }, { withCredentials: true });
      setFounderEnabled(newVal);
    } catch (err) { console.error('Settings toggle failed:', err.message); }
  };

  if (loading) return <p className="text-white/60">Loading...</p>;

  return (
    <div data-testid="admin-settings">
      <h2 className="text-2xl font-light text-white mb-6" style={{ fontFamily: 'Cormorant Garamond' }}>Platform Settings</h2>
      <div className="bg-[#0F0F14] border border-white/[0.04] p-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-white font-medium">Founder Invitation</div>
            <div className="text-white/50 text-sm mt-1">When enabled, users can register without a referral code via Founder Invitation. Requests require admin approval.</div>
          </div>
          <button data-testid="founder-toggle" onClick={toggle} className={`relative w-14 h-7 rounded-full transition-colors ${founderEnabled ? 'bg-[#C5A059]' : 'bg-white/20'}`}>
            <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white transition-transform ${founderEnabled ? 'translate-x-7' : 'translate-x-0'}`} />
          </button>
        </div>
        <div className="mt-3 text-xs text-white/40">
          Status: <span className={founderEnabled ? 'text-green-400' : 'text-red-400'}>{founderEnabled ? 'Visible on registration page' : 'Hidden from registration page'}</span>
        </div>
      </div>
    </div>
  );
};

// ─── Admin Recommendations ───
const AdminRecommendations = () => {
  const [reqs, setReqs] = useState([]);
  useEffect(() => {
    axios.get(`${API}/admin/recommendation-requests`, { withCredentials: true }).then(({ data }) => setReqs(data)).catch(() => {});
  }, []);
  return (
    <div data-testid="admin-recommendations">
      <h2 className="text-2xl font-light text-white mb-6" style={{ fontFamily: 'Cormorant Garamond' }}>Member Recommendation Requests ({reqs.length})</h2>
      {reqs.length === 0 ? <p className="text-white/40 text-sm">No requests yet.</p> : (
        <div className="space-y-3">
          {reqs.map(r => (
            <div key={r.id} className="bg-[#0F0F14] border border-white/[0.04] p-5">
              <div className="flex justify-between items-start mb-2">
                <div><span className="text-white font-medium">{r.user_name}</span><span className="text-white/40 text-xs ml-3">{r.user_email}</span></div>
                <span className={`text-xs px-2 py-1 ${r.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-green-500/20 text-green-400'}`}>{r.status}</span>
              </div>
              <div className="text-[#C5A059] text-sm mb-1">{r.category}</div>
              <div className="text-white/60 text-sm">{r.description}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Admin Community Updates ───
const AdminCommunityUpdates = () => {
  const [updates, setUpdates] = useState([]);
  const [form, setForm] = useState({ title: '', content: '', category: 'general' });
  const [editing, setEditing] = useState(null);

  const fetch = useCallback(() => {
    axios.get(`${API}/admin/community-updates`, { withCredentials: true }).then(({ data }) => setUpdates(data)).catch(() => {});
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editing) {
      await axios.put(`${API}/admin/community-updates/${editing}`, form, { withCredentials: true });
    } else {
      await axios.post(`${API}/admin/community-updates`, form, { withCredentials: true });
    }
    setForm({ title: '', content: '', category: 'general' }); setEditing(null); fetch();
  };

  const handleDelete = async (id) => { await axios.delete(`${API}/admin/community-updates/${id}`, { withCredentials: true }); fetch(); };
  const handleToggle = async (id, active) => { await axios.put(`${API}/admin/community-updates/${id}`, { is_active: !active }, { withCredentials: true }); fetch(); };

  return (
    <div data-testid="admin-community-updates">
      <h2 className="text-2xl font-light text-white mb-6" style={{ fontFamily: 'Cormorant Garamond' }}>Community Updates</h2>
      <form onSubmit={handleSubmit} className="bg-[#0F0F14] border border-white/[0.04] p-6 mb-6 space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <GoldInput data-testid="update-title" label="Title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required />
          <div><label className="block text-sm text-white/60 mb-2">Category</label>
            <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="w-full bg-[#0F0F14] border border-white/20 py-3 px-3 text-white text-sm focus:border-[#C5A059] focus:outline-none">
              <option value="general">General</option><option value="new_partner">New Partner</option><option value="offer">Member Offer</option><option value="event">Event</option><option value="category">New Category</option>
            </select>
          </div>
        </div>
        <div><label className="block text-sm text-white/60 mb-2">Content</label>
          <textarea data-testid="update-content" value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} required className="w-full bg-transparent border border-white/20 py-3 px-4 text-white text-sm focus:border-[#C5A059] focus:outline-none h-20 resize-none" />
        </div>
        <div className="flex gap-3">
          <GoldButton type="submit" data-testid="update-submit">{editing ? 'Update' : 'Publish Update'}</GoldButton>
          {editing && <GoldButton variant="secondary" type="button" onClick={() => { setEditing(null); setForm({ title: '', content: '', category: 'general' }); }}>Cancel</GoldButton>}
        </div>
      </form>
      <div className="space-y-3">
        {updates.map(u => (
          <div key={u.id} className={`bg-[#0F0F14] border ${u.is_active ? 'border-white/[0.04]' : 'border-red-500/20'} p-5 flex justify-between items-start`}>
            <div>
              <div className="text-white font-medium text-sm">{u.title} <span className="text-white/30 text-xs ml-2">{u.category}</span></div>
              <div className="text-white/50 text-xs mt-1">{u.content}</div>
              {!u.is_active && <span className="text-red-400 text-xs">Hidden</span>}
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => { setEditing(u.id); setForm({ title: u.title, content: u.content, category: u.category || 'general' }); }} className="text-[#C5A059] text-xs hover:text-[#E2C382]">Edit</button>
              <button onClick={() => handleToggle(u.id, u.is_active)} className="text-white/40 text-xs hover:text-white">{u.is_active ? 'Hide' : 'Show'}</button>
              <button onClick={() => handleDelete(u.id)} className="text-red-400/60 text-xs hover:text-red-400">Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Admin WhatsApp Analytics ───
const AdminWhatsAppAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/whatsapp-analytics`, { withCredentials: true })
      .then(({ data }) => setData(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="text-white/60">Loading...</p>;
  if (!data) return <p className="text-white/40">No tracking data available yet.</p>;

  return (
    <div data-testid="admin-whatsapp-analytics">
      <h3 className="text-lg text-white/80 mb-6" style={{ fontFamily: 'Cormorant Garamond' }}>WhatsApp Contact Tracking</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <div className="bg-[#0F0F14] border border-white/[0.04] p-6">
          <div className="text-xs text-white/40 mb-1">Total WhatsApp Clicks</div>
          <div className="text-3xl text-white font-light" style={{ fontFamily: 'Cormorant Garamond' }} data-testid="wa-total-clicks">{data.total_clicks}</div>
        </div>
        <div className="bg-[#0F0F14] border border-white/[0.04] p-6">
          <div className="text-xs text-white/40 mb-1">Last 30 Days</div>
          <div className="text-3xl text-white font-light" style={{ fontFamily: 'Cormorant Garamond' }} data-testid="wa-recent-clicks">{data.recent_30d_clicks}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-[#0F0F14] border border-white/[0.04] p-6">
          <h4 className="text-sm text-[#C5A059] uppercase tracking-wide mb-4">Most Contacted Providers</h4>
          {data.top_providers.length === 0 ? <p className="text-white/40 text-sm">No data yet</p> : (
            <div className="space-y-3">
              {data.top_providers.map((p, i) => (
                <div key={i} className="flex items-center justify-between">
                  <span className="text-white/70 text-sm">{p.provider_name || 'Unknown'}</span>
                  <span className="text-[#C5A059] text-sm font-medium">{p.clicks} clicks</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bg-[#0F0F14] border border-white/[0.04] p-6">
          <h4 className="text-sm text-[#C5A059] uppercase tracking-wide mb-4">Most Active Members</h4>
          {data.top_members.length === 0 ? <p className="text-white/40 text-sm">No data yet</p> : (
            <div className="space-y-3">
              {data.top_members.map((m, i) => (
                <div key={i} className="flex items-center justify-between">
                  <span className="text-white/70 text-sm">{m.user_name || 'Unknown'}</span>
                  <span className="text-[#C5A059] text-sm font-medium">{m.clicks} contacts</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {data.recent_activity.length > 0 && (
        <div className="bg-[#0F0F14] border border-white/[0.04] p-6">
          <h4 className="text-sm text-[#C5A059] uppercase tracking-wide mb-4">Recent Activity</h4>
          <div className="space-y-2">
            {data.recent_activity.map((a, i) => (
              <div key={i} className="flex items-center justify-between text-sm border-b border-white/[0.04] pb-2">
                <div>
                  <span className="text-white/70">{a.user_name}</span>
                  <span className="text-white/30 mx-2">contacted</span>
                  <span className="text-white/70">{a.provider_name}</span>
                </div>
                <span className="text-white/30 text-xs">{a.created_at ? new Date(a.created_at).toLocaleDateString() : ''}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════
// ADMIN PANEL
// ═══════════════════════════════════════════

const AdminPanel = () => {
  const [tab, setTab] = useState('categories');
  const [pending, setPending] = useState({});

  useEffect(() => {
    axios.get(`${API}/admin/pending-counts`, { withCredentials: true }).then(({ data }) => setPending(data)).catch(() => {});
  }, [tab]);

  const tabs = [
    { id: 'categories', label: 'Categories' },
    { id: 'providers', label: 'Service Providers' },
    { id: 'approvals', label: 'Listing Approvals' },
    { id: 'members', label: 'Members' },
    { id: 'subscriptions', label: 'Subscriptions' },
    { id: 'founder', label: 'Founder Requests' },
    { id: 'community', label: 'Community Updates' },
    { id: 'feedbacks', label: 'Feedbacks' },
    { id: 'analytics', label: 'Analytics' },
    { id: 'whatsapp', label: 'WhatsApp Tracking' },
    { id: 'settings', label: 'Settings' },
  ];

  return (
    <div className="min-h-screen bg-[#05050A]" style={{ fontFamily: 'Outfit' }}>
      <Header />
      <main className="pt-24 px-6 md:px-12 lg:px-24 py-12">
        <h1 className="text-4xl lg:text-5xl font-light text-white mb-8" style={{ fontFamily: 'Cormorant Garamond' }} data-testid="admin-heading">Admin Panel</h1>

        <div className="flex flex-wrap gap-3 mb-8" data-testid="admin-tabs">
          {tabs.map(t => {
            const count = pending[t.id] || 0;
            return (
              <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)} className={`px-6 py-3 text-sm font-medium tracking-wide transition-colors flex items-center gap-2 ${tab === t.id ? 'bg-[#C5A059] text-black' : count > 0 ? 'border border-red-400/60 text-white hover:border-red-400' : 'border border-white/20 text-white hover:border-white/60'}`}>
                {t.label}
                {count > 0 && <span data-testid={`admin-tab-badge-${t.id}`} className={`min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold flex items-center justify-center ${tab === t.id ? 'bg-black text-[#C5A059]' : 'bg-red-500 text-white'}`}>{count}</span>}
              </button>
            );
          })}
        </div>

        {tab === 'categories' && <AdminCategories />}
        {tab === 'providers' && <AdminProviders />}
        {tab === 'approvals' && <AdminListingApprovals />}
        {tab === 'members' && <AdminMembers />}
        {tab === 'subscriptions' && <AdminSubscriptions />}
        {tab === 'founder' && <AdminFounderRequests />}
        {tab === 'community' && <AdminCommunityUpdates />}
        {tab === 'feedbacks' && <AdminFeedbacks />}
        {tab === 'analytics' && <AdminAnalytics />}
        {tab === 'whatsapp' && <AdminWhatsAppAnalytics />}
        {tab === 'settings' && <AdminSettings />}
      </main>
    </div>
  );
};

export default App;
